import { useContext, useEffect, useMemo } from 'react';
import { darkGMapStyles, defaultGMapStyles } from '../../consts/GMapStyle';
import {
  DEFAULT_GEOLOCATION_OPTIONS,
  DEFAULT_MAP_COORDINATES,
  DEFAULT_ZOOM_LEVEL,
  MINIMUM_MAP_ZOOM_LEVEL
} from '../../consts/g-map-options';
import { GlobalContext } from '../context/DataContext';

const readStoredJson = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key));
  } catch {
    return null;
  }
};

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const WORLD_ICON_PATH = 'M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm6.92 6h-2.95c-.32-1.25-.82-2.45-1.5-3.5 1.93.66 3.54 1.99 4.45 3.5zM12 4c.83 1.2 1.48 2.53 1.86 4h-3.72C10.52 6.53 11.17 5.2 12 4zM4.26 14a7.8 7.8 0 010-4h3.38c-.08.66-.14 1.32-.14 2s.06 1.34.14 2H4.26zm.82 2h2.95c.32 1.25.82 2.45 1.5 3.5A8.03 8.03 0 015.08 16zm2.95-8H5.08a8.03 8.03 0 014.45-3.5C8.85 5.55 8.35 6.75 8.03 8zM12 20c-.83-1.2-1.48-2.53-1.86-4h3.72c-.38 1.47-1.03 2.8-1.86 4zm2.27-6H9.73c-.09-.66-.16-1.32-.16-2s.07-1.34.16-2h4.54c.09.66.16 1.32.16 2s-.07 1.34-.16 2zm.2 5.5c.68-1.05 1.18-2.25 1.5-3.5h2.95a8.03 8.03 0 01-4.45 3.5zM16.36 14c.08-.66.14-1.32.14-2s-.06-1.34-.14-2h3.38a7.8 7.8 0 010 4h-3.38z';

const createWorldIcon = () => {
  const icon = document.createElementNS(SVG_NAMESPACE, 'svg');
  const path = document.createElementNS(SVG_NAMESPACE, 'path');

  icon.setAttribute('aria-hidden', 'true');
  icon.setAttribute('height', '22');
  icon.setAttribute('viewBox', '0 0 24 24');
  icon.setAttribute('width', '22');
  icon.style.pointerEvents = 'none';
  path.setAttribute('d', WORLD_ICON_PATH);
  path.setAttribute('fill', 'currentColor');
  icon.appendChild(path);

  return icon;
};

const getInitialMapView = (lastVisit, mapCentralization) => {
  const lastLatitude = Number(lastVisit?.lat);
  const lastLongitude = Number(lastVisit?.lng);
  const lastZoom = Number(lastVisit?.zoom);

  if (
    lastVisit &&
    Number.isFinite(lastLatitude) &&
    Number.isFinite(lastLongitude) &&
    Number.isFinite(lastZoom)
  ) {
    return {
      center: { lat: lastLatitude, lng: lastLongitude },
      zoom: lastZoom || DEFAULT_ZOOM_LEVEL
    };
  }

  const [centralLatitude, centralLongitude] =
    mapCentralization?.location_coordinates?.split(',').map(Number) || [];

  if (
    Number.isFinite(centralLatitude) &&
    Number.isFinite(centralLongitude)
  ) {
    return {
      center: { lat: centralLatitude, lng: centralLongitude },
      zoom: Number(mapCentralization.location_zoom) || 1
    };
  }

  return {
    center: {
      lat: DEFAULT_MAP_COORDINATES.LAT,
      lng: DEFAULT_MAP_COORDINATES.LONG
    },
    zoom: DEFAULT_ZOOM_LEVEL
  };
};

export const addOverviewZoomControl = (map) => {
  if (!map || !window.google?.maps) return undefined;

  const controlPosition = window.google.maps.ControlPosition.RIGHT_BOTTOM;
  const controls = map?.controls?.[controlPosition];

  if (!controls?.push) return undefined;

  const button = document.createElement('button');
  button.type = 'button';
  button.title = 'Zoom out to world view';
  button.setAttribute('aria-label', 'Zoom out to world view');
  button.appendChild(createWorldIcon());
  Object.assign(button.style, {
    alignItems: 'center',
    backgroundColor: '#ffffff',
    border: '0',
    borderRadius: '2px',
    boxShadow: '0 1px 4px -1px rgba(0, 0, 0, 0.3)',
    color: '#3c4043',
    cursor: 'pointer',
    display: 'flex',
    height: '40px',
    justifyContent: 'center',
    margin: '10px',
    padding: '8px',
    width: '40px'
  });

  const zoomToOverview = () => map.setZoom(MINIMUM_MAP_ZOOM_LEVEL);
  button.addEventListener('click', zoomToOverview);
  controls.push(button);

  return () => {
    button.removeEventListener('click', zoomToOverview);
    const controlIndex = controls.getArray?.().indexOf(button) ?? -1;

    if (controlIndex >= 0 && controls.removeAt) {
      controls.removeAt(controlIndex);
    } else {
      button.remove();
    }
  };
};

const useGMap = (ref, map, setMap) => {
  const { latCoord, lngCoord } = useContext(GlobalContext);
  const [, setLat] = latCoord;
  const [, setLng] = lngCoord;
  const isDarkMode = useMemo(
    () => localStorage.getItem('colorSelect') === 'Mystic Night',
    []
  );
  const mapCentralization = useMemo(
    () => readStoredJson('mapCentralization'),
    []
  );
  const lastVisit = useMemo(() => readStoredJson('lastVisit'), []);
  const hasConfiguredCentralization = useMemo(() => {
    const [latitude, longitude] =
      mapCentralization?.location_coordinates?.split(',').map(Number) || [];

    return Number.isFinite(latitude) && Number.isFinite(longitude);
  }, [mapCentralization]);
  const hasStoredVisit = useMemo(
    () =>
      lastVisit &&
      Number.isFinite(Number(lastVisit.lat)) &&
      Number.isFinite(Number(lastVisit.lng)) &&
      Number.isFinite(Number(lastVisit.zoom)),
    [lastVisit]
  );
  const initialMapView = useMemo(
    () => getInitialMapView(lastVisit, mapCentralization),
    [lastVisit, mapCentralization]
  );
  const mapSettings = useMemo(
    () => ({
      zoom: initialMapView.zoom,
      maxZoom: 16,
      minZoom: MINIMUM_MAP_ZOOM_LEVEL,
      center: initialMapView.center,
      styles: isDarkMode ? darkGMapStyles : defaultGMapStyles,
      fullscreenControl: true,
      fullscreenControlOptions: {
        style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: window.google.maps.ControlPosition.RIGHT_BOTTOM
      },
      mapTypeControl: false,
      mapTypeControlOptions: {
        style: window.google.maps.MapTypeControlStyle.VERTICAL_BAR,
        position: window.google.maps.ControlPosition.BOTTOM_LEFT
      },
      zoomControl: true,
      zoomControlOptions: {
        style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: window.google.maps.ControlPosition.RIGHT_BOTTOM
      },
      scaleControl: true,
      streetViewControl: false,
      streetViewControlOptions: {
        style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
        position: window.google.maps.ControlPosition.RIGHT_BOTTOM
      },
      keyboardShortcuts: false
    }),
    [initialMapView, isDarkMode]
  );

  useEffect(() => {
    if (map) {
      return;
    }

    let animationFrame;
    let mapCreated = false;
    let resizeObserver;

    const initializeMap = () => {
      const mapContainer = ref.current;

      if (!mapContainer || mapCreated) {
        return mapCreated;
      }

      const { height, width } = mapContainer.getBoundingClientRect();

      if (!height || !width) {
        return false;
      }

      mapCreated = true;
      resizeObserver?.disconnect();
      setLat(initialMapView.center.lat);
      setLng(initialMapView.center.lng);
      setMap(new window.google.maps.Map(mapContainer, mapSettings));

      return true;
    };

    const initializeOnAnimationFrame = () => {
      animationFrame = window.requestAnimationFrame(() => {
        if (!initializeMap()) {
          initializeOnAnimationFrame();
        }
      });
    };

    if (window.ResizeObserver && ref.current) {
      resizeObserver = new window.ResizeObserver(initializeMap);
      resizeObserver.observe(ref.current);
    }

    initializeOnAnimationFrame();

    return () => {
      mapCreated = true;
      resizeObserver?.disconnect();
      window.cancelAnimationFrame(animationFrame);
    };
  }, [initialMapView, map, mapSettings, ref, setLat, setLng, setMap]);

  useEffect(() => {
    if (!map) {
      return;
    }

    let animationFrame;
    let resizeObserver;

    const resizeMap = () => {
      window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(() => {
        const mapContainer = ref.current;

        if (!mapContainer) {
          return;
        }

        const { height, width } = mapContainer.getBoundingClientRect();

        if (!height || !width) {
          return;
        }

        const center = map.getCenter();
        window.google.maps.event.trigger(map, 'resize');

        if (center) {
          map.setCenter(center);
        }
      });
    };

    if (window.ResizeObserver && ref.current) {
      resizeObserver = new window.ResizeObserver(resizeMap);
      resizeObserver.observe(ref.current);
    }

    resizeMap();

    return () => {
      resizeObserver?.disconnect();
      window.cancelAnimationFrame(animationFrame);
    };
  }, [map, ref]);

  useEffect(() => addOverviewZoomControl(map), [map]);

  useEffect(() => {
    if (
      !map ||
      hasStoredVisit ||
      hasConfiguredCentralization ||
      !navigator.geolocation
    ) {
      return;
    }

    let isActive = true;

    navigator.geolocation.getCurrentPosition(
      (position) => {
        if (!isActive) {
          return;
        }

        const center = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };

        setLat(center.lat);
        setLng(center.lng);
        map.setCenter(center);
        map.setZoom(DEFAULT_ZOOM_LEVEL);
      },
      undefined,
      DEFAULT_GEOLOCATION_OPTIONS
    );

    return () => {
      isActive = false;
    };
  }, [hasConfiguredCentralization, hasStoredVisit, map, setLat, setLng]);
};

export default useGMap;
